import { getLocaleCodeByCountryCode } from '@zep-us/i18n';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { type ParsedUrlQuery } from 'querystring';

import '../tracking/events.quiz-inquiry';

import { trackEvent } from '@zep/logger/tracking';
import { getInAppBrowserName, isInAppBrowser } from '@zep/utils/device';

import { useLocale } from '@/hooks';
import type { SchoolCtaEvent } from '@/shared/consts/events.quiz-school-package';
import { useCountryCode } from '@/shared/hooks';
import { navigateSameTab } from '@/shared/utils/navigateSameTab';
import {
  appendTrackingId,
  appendUTMParams,
  createTrackingId,
  parseTrackingId,
  TRACKING_ID_PARAM,
} from '@/utils/analytics';

import {
  type InquiryCtaLocation,
  type InquiryFormOpenMethod,
} from '../tracking/events.quiz-inquiry';

const DEFAULT_INQUIRY_URL = 'https://tally.so/r/wvLX7X';

/** 해당 언어에 폼이 없으면 영문 폼으로 폴백한다. */
const FALLBACK_LOCALE = 'en';

/** 내부 라우팅으로 여는 Salesmap 임베드 폼 경로. */
const SALES_EMBED_PATHNAME = '/embed/sales';

/** 내부 URL 이 임베드 폼 페이지를 가리키는지(= 이동하면 실제로 폼이 열리는지). */
const isSalesEmbedUrl = (url: string) => {
  try {
    return (
      new URL(url, window.location.origin).pathname === SALES_EMBED_PATHNAME
    );
  } catch {
    return false;
  }
};

/**
 * 내부 라우팅 URL 에 `tracking_id` 를 실어 보낸다.
 *
 * 두 가지 경우에만 붙인다.
 * 1. **임베드 폼 페이지**(`/embed/sales`) — 폼이 이 쿼리를 읽어 제출 데이터로 넘긴다. 항상.
 * 2. **폼이 없는 중간 페이지**(`/pricing`) — `propagate` 가 켜진 CTA 일 때만.
 *    랜딩처럼 "일단 가격 페이지로 보낸 뒤 거기서 문의하는" 여정을 하나로 잇기 위한 것으로,
 *    도착 페이지의 문의 CTA 가 이 값을 이어받는다(아래 `openInquiryUrl` 참고).
 *
 * `propagate` 를 기본값 false 로 둔 이유: `/pricing` 은 도입문의 외의 CTA(예: PayPal 결제
 * 문의)도 보내는 공용 목적지라, 무조건 붙이면 **다른 퍼널의 클릭이 도입문의 제출에 잘못
 * 조인**된다.
 */
const appendTrackingIdToInternalUrl = (
  url: string,
  trackingId: string,
  { propagate }: { propagate: boolean },
) => {
  try {
    const urlObj = new URL(url, window.location.origin);
    const isSalesEmbed = urlObj.pathname === SALES_EMBED_PATHNAME;
    if (!isSalesEmbed && !propagate) return url;

    urlObj.searchParams.set(TRACKING_ID_PARAM, trackingId);
    // next/router 에는 상대 경로로 넘긴다(절대 URL 은 외부 이동으로 취급될 수 있음).
    return `${urlObj.pathname}${urlObj.search}`;
  } catch {
    return url;
  }
};

/**
 * 현재 페이지가 이전 클릭에서 이어받은 `tracking_id`.
 * 있으면 이번 클릭도 같은 id 를 써서 "랜딩 클릭 → 가격 페이지 → 문의 제출"을 한 여정으로 묶는다.
 *
 * 공개 URL 에서 오는 값이라 **형식 검증을 통과한 것만** 승계한다(`parseTrackingId`).
 * 임의 문자열을 그대로 받으면 분석 이벤트·GTM·외부 폼까지 흘러간다.
 */
const getInheritedTrackingId = (query: ParsedUrlQuery) =>
  parseTrackingId(query[TRACKING_ID_PARAM]);

/**
 * 현재 언어와 영어 폴백에 모두 값이 없으면 t() 가 키 문자열을 그대로 돌려준다.
 * 깨진 상대 경로로 열리지 않도록 절대 URL 만 통과시킨다.
 */
const toUrl = (value: string) => {
  const url = value.trim();
  return url.startsWith('http') ? url : undefined;
};

interface UseInquiryUrlOptions {
  /** KR일 때 사용할 내부 URL (예: '/pricing', '/embed/sales?type=inquiry') */
  krUrl?: string;
  /** 외부에서 countryCode를 주입 (제공하지 않으면 useCountryCode에서 가져옴) */
  countryCode?: string;
  /**
   * 문의 폼 종류.
   * - 'default': 기존 공통 문의 링크 (`common:paypal.help`)
   * - 'group': 학교·단체 전용 문의 폼 (`pricing:groupPricing.default.inquiryUrl`)
   * - 'pricing': Pricing 일반 문의 폼 (`common:pricing.pricing.contactLink`)
   */
  source?: 'default' | 'group' | 'pricing';
  /** 지정된 외부 문의 폼이 있으면 언어별 기본 URL 대신 사용한다. */
  externalUrl?: string;
  /**
   * 폼이 없는 내부 경로(`/pricing`)로 보낼 때도 이번 클릭의 `tracking_id` 를 URL 로 넘길지.
   *
   * 랜딩 "우리 학교에 도입하기"처럼 **가격 페이지를 거쳐 문의로 이어지는** CTA 에만 켠다.
   * 도착 페이지의 문의 CTA 가 이 id 를 이어받아, 한 제출이 랜딩 클릭까지 조인된다.
   *
   * 기본값 false — `/pricing` 은 도입문의 외의 CTA(PayPal 결제 문의 등)도 보내는 공용
   * 목적지라, 무조건 켜면 다른 퍼널의 클릭이 도입문의 제출에 잘못 붙는다.
   */
  propagateTrackingIdOnInternalNav?: boolean;
}

/**
 * 이 클릭의 분석 이벤트를 찍는 콜백. 훅이 **생성한** `trackingId` 를 넘겨준다.
 *
 * 클릭 이벤트와 문의폼 URL 이 반드시 같은 값을 쓰도록 생성 지점을 훅 하나로 모았다.
 * (호출부에서 각자 만들어 넘기면 두 값이 어긋나도 컴파일러가 잡아주지 못한다)
 * 넘기지 않으면 이벤트는 찍지 않고 폼 URL 에만 `tracking_id` 를 싣는다.
 */
type OnInquiryClick = (trackingId: string) => void;

interface OpenInquiryUrlOptions {
  schoolContext?: SchoolCtaEvent;
  /**
   * 이 CTA 의 위치. **필수** — 빠뜨리면 9곳의 폼 열림/팝업 차단 지표가 한 덩어리로
   * 섞이므로, 옵션이 아니라 컴파일 에러로 막는다.
   */
  location: InquiryCtaLocation;
  onInquiryClick?: OnInquiryClick;
}

/**
 * 문의 URL을 여는 훅
 * - KR 기본 문의: 내부 라우팅 (기존 동작 유지)
 * - KR + 한국어 Pricing 문의: 내부 라우팅
 * - 그 외: 시트에서 URL을 가져와 새 탭으로 열기
 *
 * Pricing 문의(source: 'group' | 'pricing')는 표시 언어의 폼을 쓰고,
 * 해당 언어에 값이 없으면 영문 폼으로 폴백한다.
 *
 * 열리는 모든 문의 폼 URL 에는 클릭 1회를 가리키는 `tracking_id` 를 실어, 폼(CRM) 제출
 * 데이터와 클릭 이벤트를 사후 대조할 수 있게 한다(UTM 이 끊겨도 전환을 잇는 용도).
 *
 * 단, 현재 URL 이 이전 클릭에서 넘겨받은 `tracking_id` 를 갖고 있으면 새로 만들지 않고
 * **그 값을 이어받는다**. 랜딩 → 가격 페이지 → 문의 제출을 한 여정으로 묶기 위한 것으로,
 * 이때는 제출 1건이 랜딩 클릭과 가격 페이지 클릭 양쪽에 조인된다(의도된 동작).
 */
export function useInquiryUrl({
  krUrl = '/pricing',
  countryCode: externalCountryCode,
  source = 'default',
  externalUrl,
  propagateTrackingIdOnInternalNav = false,
}: UseInquiryUrlOptions) {
  const router = useRouter();
  const { countryCode: userCountryCode, isKR: userIsKR } = useCountryCode();
  const { locale } = useLocale();
  const { i18n } = useTranslation(['common', 'pricing']);

  const countryCode = externalCountryCode ?? userCountryCode;
  const isKR = externalCountryCode ? externalCountryCode === 'KR' : userIsKR;

  const openInquiryUrl = async ({
    location,
    onInquiryClick,
    schoolContext,
  }: OpenInquiryUrlOptions) => {
    // 이전 클릭에서 이어받은 id 가 있으면 그것을, 없으면 이번 클릭의 id 를 새로 만든다.
    // 아래 모든 분기가 이 하나의 값을 공유한다.
    const trackingId =
      getInheritedTrackingId(router.query) ?? createTrackingId();

    // `onClick={openInquiryUrl}` 처럼 바로 넘기면 첫 인자로 React 이벤트 객체가 들어온다.
    // 그 경우는 "콜백 없음"으로 취급해야 호출부가 터지지 않는다.
    if (typeof onInquiryClick === 'function') {
      onInquiryClick(trackingId);
    }

    const inAppBrowser = isInAppBrowser();
    const trackFormOpen = (openMethod: InquiryFormOpenMethod) =>
      trackEvent('quiz_inquiry_form_open', {
        ...schoolContext,
        tracking_id: trackingId,
        cta_location: location,
        open_method: openMethod,
        in_app_browser: inAppBrowser,
        browser_name: inAppBrowser ? getInAppBrowserName() : 'none',
      });

    /** 외부 문의 폼을 연다. 유입 UTM + 이번 클릭의 tracking_id 를 함께 싣는다. */
    const openExternalForm = (url: string) => {
      const formUrl = appendTrackingId(appendUTMParams(url), trackingId);

      // features 에 `noopener` 를 주면 성공/차단과 무관하게 null 이 돌아와 **실패를 감지할 수
      // 없다**. 대신 열린 창의 opener 를 끊어 같은 보호(reverse tabnabbing 방지)를 유지한다.
      // `noreferrer` 도 함께 뺐는데, 문의 폼(CRM)이 referrer 를 유입 정보로 저장하므로 오히려 낫다.
      const openedWindow = window.open(formUrl, '_blank');

      if (openedWindow) {
        openedWindow.opener = null;
        trackFormOpen('popup');
        return;
      }

      // 팝업 차단(인앱 웹뷰·iOS Safari 에서 흔하다. 특히 위 await 로 유저 제스처 컨텍스트가
      // 끊긴 뒤라면 더). 지금까지는 여기서 조용히 아무 일도 일어나지 않아 문의가 통째로
      // 유실됐다 — 같은 탭 이동으로 폼은 반드시 열리게 한다.
      trackFormOpen('same_tab_fallback');
      navigateSameTab(formUrl);
    };

    if (externalUrl) {
      openExternalForm(externalUrl);
      return;
    }

    if (isKR && (source === 'default' || locale === 'ko')) {
      const internalUrl = appendTrackingIdToInternalUrl(krUrl, trackingId, {
        propagate: propagateTrackingIdOnInternalNav,
      });

      try {
        // 라우팅이 **성공한 뒤에만** 폼 열림으로 센다. 빠른 중복 클릭으로 첫 이동이
        // 취소되거나 라우트 로딩이 실패해도 열린 것으로 집계되면 퍼널이 부풀려진다.
        const navigated = await router.push(internalUrl);

        // 폼이 있는 임베드 경로일 때만 센다(`/pricing` 은 단순 이동).
        if (navigated && isSalesEmbedUrl(krUrl)) {
          trackFormOpen('internal');
        }
      } catch {
        // next/router 의 push 는 false 를 돌려주는 것 말고 **reject 도 한다**
        // (연속 탐색으로 이전 이동이 취소되거나 라우트 청크 로드 실패).
        // 호출부가 전부 `void openInquiryUrl(...)` 라 여기서 새어나가면 잡는 사람이 없어
        // unhandled rejection 이 된다. 실패 = 폼이 열리지 않은 것이므로 계측도 생략한다.
        // (라우팅 에러 자체는 Next 의 routeChangeError 경로가 이미 다룬다)
      }
      return;
    }

    if (source !== 'default') {
      const isGroupInquiry = source === 'group';
      const namespace = isGroupInquiry ? 'pricing' : 'common';
      const inquiryKey = isGroupInquiry
        ? 'pricing:groupPricing.default.inquiryUrl'
        : 'common:pricing.pricing.contactLink';

      try {
        await i18n.reloadResources([locale, FALLBACK_LOCALE], namespace);
      } catch {
        // 리소스 갱신에 실패해도 이미 로드된 값으로 시도한다.
      }

      const inquiryUrl =
        toUrl(i18n.t(inquiryKey, { lng: locale })) ??
        toUrl(i18n.t(inquiryKey, { lng: FALLBACK_LOCALE })) ??
        DEFAULT_INQUIRY_URL;

      openExternalForm(inquiryUrl);
      return;
    }

    try {
      const inquiryLocale = getLocaleCodeByCountryCode(countryCode);
      await i18n.reloadResources(inquiryLocale, 'common');
      const inquiryUrl = i18n.t('common:paypal.help');

      if (!inquiryUrl) {
        openExternalForm(DEFAULT_INQUIRY_URL);
        return;
      }

      openExternalForm(inquiryUrl);
    } catch {
      openExternalForm(DEFAULT_INQUIRY_URL);
    }
  };

  return { openInquiryUrl };
}
